import type { ReactNode } from 'react';

export interface EmptyStateProps {
  readonly title: string;
  readonly description?: string;
  /** Decorative glyph shown in the circular holder. */
  readonly icon?: ReactNode;
  /** Typically a `Button`, or the prototypes' soft accent link-button. */
  readonly action?: ReactNode;
}

/**
 * Empty state (§7.1 primitives), reproducing the storefront prototype's empty
 * cart panel: a 3.5rem `--ecom-surface-2` circle holding an `--ecom-ink-faint`
 * glyph, a `--ecom-ink` title, an `--ecom-ink-faint` description, and an
 * optional action beneath.
 *
 * The heading is a `<p>`, not an `<h2>`. An empty state appears *inside* a
 * region that already has its own heading ("Panier", "Commandes"), and
 * emitting a second heading there would corrupt the document outline that
 * screen-reader users navigate by. The visual weight comes from styling.
 *
 * The icon is `aria-hidden`: it repeats the title (an empty-cart glyph above
 * "Votre panier est vide") and announcing both is redundant.
 */
export function EmptyState({ title, description, icon, action }: EmptyStateProps): ReactNode {
  return (
    <div className="ecom-empty">
      {icon !== undefined ? (
        <span className="ecom-empty__icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}

      <p className="ecom-empty__title">{title}</p>

      {description !== undefined ? <p className="ecom-empty__description">{description}</p> : null}

      {action !== undefined ? <div className="ecom-empty__action">{action}</div> : null}
    </div>
  );
}
